// src/components/Seo.jsx
import { useEffect, useContext } from "react";
import { SITE_URL, BRAND } from "../config/site";
import { LangContext } from "../lang/LangProvider";

function set(name, content) {
  if (!content) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function setProperty(prop, content) {
  if (!content) return;
  let el = document.querySelector(`meta[property="${prop}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("property", prop); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function ensureLink(rel, hreflang, href) {
  if (!href) return;
  let selector = `link[rel="${rel}"]`;
  if (hreflang) selector += `[hreflang="${hreflang}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
function setCanonical(href) {
  ensureLink("canonical", null, href);
}

const injectJsonLd = (json) => {
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.text = JSON.stringify(json);
  document.head.appendChild(s);
  return () => document.head.removeChild(s);
};

export default function Seo({
  title,
  description,
  path = "/",
  type = "website",   // "website" | "article"
  image,              // assoluto o relativo
  noindex = false,
  jsonLd,             // opzionale: oggetto JSON-LD
  hreflang = true,    // abilita tag alternate hreflang it/en
}) {
  const { lang } = useContext(LangContext); // definisce og:locale e supporto lang

  useEffect(() => {
    const url = path.startsWith("http") ? path : `${SITE_URL}${path}`;
    const pageTitle = title ? `${title} | ${BRAND.name}` : BRAND.name;
    document.title = pageTitle;

    // Base meta
    set("description", description);
    set("robots", noindex ? "noindex, nofollow" : "index, follow");

    // Open Graph
    setProperty("og:type", type);
    setProperty("og:title", pageTitle);
    setProperty("og:description", description);
    setProperty("og:url", url);
    setProperty("og:site_name", BRAND.name);

    // Locale (importante per lingua)
    const ogLocale = lang === "en" ? "en_US" : "it_IT";
    const ogAlt = lang === "en" ? "it_IT" : "en_US";
    setProperty("og:locale", ogLocale);
    setProperty("og:locale:alternate", ogAlt);

    if (image) setProperty("og:image", image.startsWith("http") ? image : `${SITE_URL}${image}`);

    // Twitter
    set("twitter:card", "summary_large_image");
    set("twitter:title", pageTitle);
    set("twitter:description", description);
    if (image) set("twitter:image", image.startsWith("http") ? image : `${SITE_URL}${image}`);

    // Canonical
    setCanonical(url);

    // hreflang (se la rotta contiene prefisso lingua)
    if (hreflang) {
      const m = path.match(/^\/(it|en)(\/.*)?$/i);
      if (m) {
        const curLang = m[1].toLowerCase(); // "it" | "en"
        const tail = (m[2] || "") || "/";
        const hrefIt = `${SITE_URL}/it${tail}`;
        const hrefEn = `${SITE_URL}/en${tail}`;
        ensureLink("alternate", "it", hrefIt);
        ensureLink("alternate", "en", hrefEn);
        // x-default = preferita (mettiamo IT come default, puoi switcharla)
        ensureLink("alternate", "x-default", hrefIt);
      }
    }

    let cleanup = () => {};
    if (jsonLd) cleanup = injectJsonLd(jsonLd);
    return cleanup;
  }, [title, description, path, type, image, noindex, hreflang, lang, jsonLd]);

  return null;
}
